import { useSkuStore } from "~/providers/sku-store-provider"
import { api } from "~/utils/api"

export function useRawVariants(enabled: boolean = true) {
  // activatedFrom Dates never compare equal, so sharing never keeps the root and only costs the walk.
  return api.variantTableData.variants.useQuery({}, { enabled, structuralSharing: false })
}

export function useAllStockOutTimes(enabled: boolean = true) {
  return api.productTableData.stockOutTimesV2.useQuery({}, { enabled })
}

export function useVariantSettings(enabled: boolean = true) {
  return api.variantTableData.settings.useQuery(undefined, { enabled })
}

export function useSalesSummaries(
  warehouseId: string | undefined,
  channel: string | undefined,
  enabled: boolean = true,
) {
  return api.variantTableData.getSalesSummaries.useQuery({ warehouseId, channel }, { enabled })
}

export function useStockLevels(enabled: boolean = true) {
  return api.variantTableData.stockLevels.useQuery(undefined, { enabled })
}

export function useIncomingOrders(
  enabled: boolean = true,
  status: "incoming" | "arrived" = "incoming",
) {
  return api.variantTableData.incomingOrders.useQuery({ status }, { enabled })
}

export function useForecastBreakdown(enabled: boolean = true) {
  return api.variantTableData.forecastBreakdown.useQuery(undefined, { enabled })
}

export function useForecastBreakdownDetail(variantIds: string[], enabled: boolean) {
  return api.variantTableData.forecastBreakdownDetail.useQuery(
    { variantIds },
    { enabled: enabled && variantIds.length > 0 },
  )
}

export function usePhaseOutSellDownDetail(variantIds: string[], enabled: boolean) {
  return api.variantTableData.phaseOutSellDownDetail.useQuery(
    { variantIds },
    { enabled: enabled && variantIds.length > 0 },
  )
}

export function useForecastDecomposition(enabled: boolean = true) {
  return api.variantTableData.forecastDecomposition.useQuery(undefined, { enabled })
}

export function useFutureKnownPreorders(warehouseId: string | undefined, enabled: boolean = true) {
  return api.variantTableData.futureKnownPreorders.useQuery({ warehouseId }, { enabled })
}

export function useInventoryTurnData(enabled: boolean = true) {
  return api.variantTableData.getInventoryTurnData.useQuery(undefined, { enabled })
}

export function useSkuInventoryTurnData(
  skuIds: string[],
  warehouseId: string | undefined,
  enabled: boolean = true,
) {
  return api.sku.getSkuInventoryTurnData.useQuery(
    { skuIds, warehouseId },
    { enabled: enabled && skuIds.length > 0 },
  )
}
export function useSummedForecastedReturns(
  warehouseId: string | undefined,
  channel: string | undefined,
  enabled: boolean = true,
) {
  return api.expectedReturns.getSummedForecastedReturns.useQuery(
    { warehouseId, channel },
    { enabled },
  )
}

// Triggers the fetches for ProductTableV2-only secondary fields
// (forecastAlertAggregate, forecastedReturnsQuantity, futureKnownPreorders). Call
// from components that render those fields so the queries only fire on routes that
// need them. useVariantTableDataHeavy observes the resulting cache and merges the
// data into the SKU store. Warehouse/channel come from the SKU store to match the
// cache keys the observer uses.
export function useEnsureProductTableSecondaryData() {
  const warehouseId = useSkuStore((s) => s.warehouseId)
  const channel = useSkuStore((s) => s.channel)
  useForecastBreakdown()
  useSummedForecastedReturns(warehouseId, channel)
  useFutureKnownPreorders(warehouseId)
}

// Lighter variant for components that only consume forecastAlertAggregate
// (ProductInformationCard, ForecastBreakdownHoverCard outside the table).
export function useEnsureForecastBreakdownData() {
  useForecastBreakdown()
}
